import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import FileUpload, { SecondaryText } from 'components/common/FileUpload';
import {
  setEnrichmentFile,
  setEnrichmentData,
  updateEnrichmentSettings,
  setEnrichedListDetails,
  setEnrichmentError,
} from 'actions/enrichmentWizard';
import Icon from 'components/common/Icon';
import {
  getEnrichmentFile,
  getEnrichmentData,
  getEnrichmentSettings,
  getEnrichmentListDetails,
  getEnrichmentError,
  getEnrichmentType,
} from 'selectors/enrichmentWizard';
import ButtonSwitch from 'components/common/ButtonSwitch';
import { ListNameInput } from 'components/common';
import {
  CloseButton,
  DownloadLink,
  ErrorBox,
  ErrorBoxSpace,
  HeaderText,
} from './styled';

const XLSX = import('xlsx');

const UploadFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 8px;
  margin-bottom: 16px;
  overflow: visible;
  text-wrap: nowrap;
`;

const UploadContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 90%;
  flex-direction: column;
  align-items: center;
`;

const FilePreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  gap: 16px;
`;

const FilePreview = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  width: 200px;
  height: 200px;
`;

const FileIcon = styled.div`
  font-size: 48px;
  color: #2196f3;
  margin-bottom: 5px;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: -8px;
  right: -8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background-color: #ff4444;
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  padding: 0;

  &:hover {
    background-color: #cc0000;
  }
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 500px;
`;

const LinkHighlight = styled.span`
  color: #2196f3;
  text-decoration: underline;
  cursor: pointer;
`;

const FilenameText = styled.div`
  font-size: 14px;
  word-break: break-all;
`;

const FilenameTextContainer = styled.div`
  width: 100%;
  overflow: hidden;
  text-align: center;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  width: 100%;
  margin-top: 32px;
`;
const OptionRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 50px;
`;

const OptionSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #e0e0e0;
  margin: 16px 0;
`;

const ListNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ListNameInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const fileExtensions = ['.xlsx', '.csv'];
const maxFileSize = 10;

const errorMessages = {
  'Invalid file type': `Your file format is not supported. Please upload a ${fileExtensions.join(
    ' or '
  )} file.`,
  'File too large': `Your file is larger than our ${maxFileSize}MB limit. Please upload a smaller file.`,
};

const UploadStep = () => {
  const dispatch = useDispatch();
  const file = useSelector(getEnrichmentFile);
  const data = useSelector(getEnrichmentData);
  const settings = useSelector(getEnrichmentSettings);
  const listDetails = useSelector(getEnrichmentListDetails);
  const [listName, setListName] = useState(listDetails?.listName || '');
  const type = useSelector(getEnrichmentType);
  const error = useSelector(getEnrichmentError);

  useEffect(() => {
    dispatch(
      setEnrichedListDetails({
        listName,
      })
    );
  }, [dispatch, listName]);

  useEffect(() => {
    dispatch(setEnrichmentError(null));

    return () => {
      dispatch(setEnrichmentError(null));
    };
  }, [dispatch]);

  const handleRemoveFile = () => {
    dispatch(setEnrichmentFile(null));
    dispatch(setEnrichmentData([]));
  };

  const handleFileChange = async newfile => {
    dispatch(setEnrichmentFile(newfile));
    dispatch(setEnrichmentData([]));
  };

  const handleHeaderToggle = val => {
    dispatch(updateEnrichmentSettings({ includeHeaders: !val }));
  };

  const handleDownloadSample = async () => {
    const xlsx = await XLSX;
    const wb = xlsx.utils.book_new();
    const ws = xlsx.utils.aoa_to_sheet([
      [type === 'email' ? 'Email' : 'Organisation'],
    ]);
    xlsx.utils.book_append_sheet(
      wb,
      ws,
      type === 'email' ? 'Emails' : 'Organisations'
    );
    const excelBuffer = xlsx.write(wb, { bookType: 'xlsx', type: 'array' });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_template.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleOpenFaq = () => {
    const { fcWidget } = window;
    if (fcWidget) {
      fcWidget.open();
    }
  };

  const handleUnsetError = () => {
    dispatch(setEnrichmentError(null));
  };

  const onUploadError = error => {
    dispatch(
      setEnrichmentError(
        errorMessages[error] || (error && 'Something went wrong')
      )
    );
  };

  return (
    <UploadContainer>
      <ErrorBoxSpace hasError={!!error}>
        {error && (
          <ErrorBox>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Icon icon='error' size='sm' color='red' />
              <div>{error}</div>
            </div>
            <CloseButton onClick={handleUnsetError}>
              <Icon icon='close' size='lg' color='grey' />
            </CloseButton>
          </ErrorBox>
        )}
      </ErrorBoxSpace>
      {file && data?.length ? (
        <>
          <FilePreviewContainer>
            <FilePreview>
              <RemoveButton onClick={handleRemoveFile}>
                <Icon icon='close' size='sm' color='white' />
              </RemoveButton>
              <FileIcon>
                <Icon icon='fileExcel' size='sm' color='green' />
              </FileIcon>
              <FilenameTextContainer>
                <FilenameText>{file.name}</FilenameText>
              </FilenameTextContainer>
            </FilePreview>
          </FilePreviewContainer>
          <OptionsContainer>
            <OptionRow>
              <HeaderText>Does your file have headers?</HeaderText>
              <ButtonSwitch
                checked={!settings.includeHeaders}
                onChange={handleHeaderToggle}
              />
            </OptionRow>
            <OptionSeparator />
            <OptionRow>
              <ListNameWrapper>
                <ListNameInputContainer>
                  <HeaderText>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      List Name <SecondaryText>(optional)</SecondaryText>
                    </div>
                  </HeaderText>
                  <ListNameInput
                    id='listName'
                    value={listName}
                    onChange={setListName}
                    autoComplete='off'
                    rows={1}
                  />
                </ListNameInputContainer>
                <SecondaryText>
                  You can share and edit the list once it has been created
                </SecondaryText>
              </ListNameWrapper>
            </OptionRow>
          </OptionsContainer>
        </>
      ) : (
        <FileUploadContainer>
          <FileUpload
            fileExtensions={fileExtensions}
            maxFileSize={maxFileSize}
            icon='upload'
            text={
              <div>
                Drag and drop file here or{' '}
                <LinkHighlight>click to upload</LinkHighlight>
              </div>
            }
            onUpload={handleFileChange}
            height='300px'
            width='450px'
            isUploading={!!file}
            enforceFileType={false}
            onError={onUploadError}
          >
            <SecondaryText>
              You can only upload one file at a time
            </SecondaryText>
          </FileUpload>
          <UploadFooter>
            <DownloadLink onClick={handleDownloadSample}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <Icon icon='download' size='sm' color='inherit' />
                {type === 'email'
                  ? 'Email Enrichment Template (.xlsx)'
                  : 'Organisation Enrichment Template (.xlsx)'}
              </div>
            </DownloadLink>
            <div>Upload Limit: 5K rows (10MB)</div>
          </UploadFooter>
          <SecondaryText>
            <div style={{ textAlign: 'center' }}>
              SciLeads does not retain your uploaded data. See{' '}
              <LinkHighlight onClick={handleOpenFaq}>here</LinkHighlight> for
              more details
            </div>
          </SecondaryText>
        </FileUploadContainer>
      )}
    </UploadContainer>
  );
};

export default UploadStep;
